// Events hook for React Native admin app
// Based on admin-web events implementation and mobile app patterns

import { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  useGetEventsQuery,
  useLazyGetEventsLazyQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
} from '@/store/api/admin/eventsApi';
import { 
  IEvent, 
  IEventTableRow, 
  IEventFilterModel, 
  IEventSortModel,
  EventViewType,
  EVENT_VIEW_TYPES,
  EventStatusType,
  EVENT_STATUSES,
  CALENDAR_KEYS,
  IDayEventPairings,
} from '@/types/admin/events';
import { IPaginationModel } from '@/types/admin/dataTable';
import { useAdminUser } from './useAdminUser';
import { createEnhancedError } from '@/utils/admin/errorHandling';
import { ROWS_PER_PAGE } from '@/constants/admin';
import { useDataTableState } from '@/contexts/admin/DataTableContext';

// Default filter model
const defaultFilterModel: IEventFilterModel = {
  items: [],
  status: [],
  dateRange: {},
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
};

// Default sort model
const defaultSortModel: IEventSortModel[] = [
  { field: 'startTime', sort: 'desc' }
];

// Default pagination model
const defaultPaginationModel: IPaginationModel = {
  page: 0,
  pageSize: ROWS_PER_PAGE,
};

// Utility functions (matching admin-web utils)
export const formatEventType = (event: IEvent): EventStatusType => {
  const now = new Date();

  if (new Date(event.startTime || '') < now) {
    return EVENT_STATUSES.PAST;
  }
  if (!event.publishTime) {
    return EVENT_STATUSES.DRAFT;
  }
  
  const publishTime = new Date(event.publishTime);
  
  if (publishTime <= now) {
    return EVENT_STATUSES.PUBLISHED;
  }
  return EVENT_STATUSES.SCHEDULED;
};

export const generateDayEventList = (monthlyEvents: IEvent[]): IDayEventPairings => {
  const dayEventPairings: IDayEventPairings = {};
  monthlyEvents.forEach((event: IEvent) => {
    // Bucket by the property's local date (startTimeLocal is ISO-with-offset
    // from backend moment().tz(propertyTimezone).format()). Slicing startTime
    // instead would use UTC, which rolls events past ~8pm local into the next day.
    if (event.startTimeLocal) {
      const dateKey = event.startTimeLocal.slice(0, 10);
      if (dayEventPairings[dateKey]) {
        dayEventPairings[dateKey].push(event);
      } else {
        dayEventPairings[dateKey] = [event];
      }
    }
  });
  return dayEventPairings;
};

export const formatAttendeeLimit = (attendeeLimit: number): string => {
  return attendeeLimit === 0 ? 'Unlimited' : attendeeLimit.toString();
};

export const useEvents = () => {
  // Get admin user data (including selected property)
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  // Local state for UI controls
  const [view, setView] = useState<EventViewType>(EVENT_VIEW_TYPES.CALENDAR);
  const table = useDataTableState('admin:events');
  const searchQuery = table.searchQuery;
  const filterModel = ((table.filterModel as unknown) as IEventFilterModel) || defaultFilterModel;
  const sortModel = ((table.sortModel as unknown) as IEventSortModel[]) || defaultSortModel;
  const paginationModel = (table.paginationModel as IPaginationModel) || defaultPaginationModel;
  
  // Calendar-specific state (matching admin-web)
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
  
  // Retry state
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // API queries
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = useGetEventsQuery(
    {
      sortModel,
      filterModel,
      paginationModel,
      selectedPropertyId: selectedPropertyId!,
      searchQuery,
    },
    {
      skip: !selectedPropertyId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Lazy query for manual triggering (matching admin-web pattern)
  const [getEventsList, { data: lazyEventsData }] = useLazyGetEventsLazyQuery();

  // Mutations
  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();

  // Enhanced error handling
  const enhancedError = useMemo(() => {
    if (!eventsError) return null;
    return createEnhancedError(eventsError as any, 'events');
  }, [eventsError]);

  // Transform events data to table rows
  const tableRows = useMemo((): IEventTableRow[] => {
    const events = eventsData?.data || lazyEventsData || [];
    if (!Array.isArray(events)) return [];
    
    return events.map((event: IEvent): IEventTableRow => ({
      id: event.id,
      name: event.name,
      startTime: event.startTime,
      endTime: event.endTime,
      location: event.location,
      rsvpLimit: event.rsvpLimit,
      n_rsvps: event.n_rsvps,
      status: formatEventType(event),
      publishTime: event.publishTime,
      createdAt: event.createdAt,
    }));
  }, [eventsData, lazyEventsData]);

  // Filter events by selected month (for calendar view).
  // Filter must use the same property-local clock as the bucket keys, otherwise
  // events near month boundaries can be filtered-in but have no matching cell.
  const monthlyEvents = useMemo((): IEvent[] => {
    const events = eventsData?.data || lazyEventsData || [];
    if (!Array.isArray(events)) return [];

    const monthKey = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;
    return events.filter((event: IEvent) => event.startTimeLocal?.slice(0, 7) === monthKey);
  }, [eventsData, lazyEventsData, selectedMonth]);

  // Month-filtered table rows derived from monthlyEvents
  const monthFilteredRows = useMemo((): IEventTableRow[] => {
    return monthlyEvents.map((event: IEvent): IEventTableRow => ({
      id: event.id,
      name: event.name,
      startTime: event.startTime,
      endTime: event.endTime,
      location: event.location,
      rsvpLimit: event.rsvpLimit,
      n_rsvps: event.n_rsvps,
      status: formatEventType(event),
      publishTime: event.publishTime,
      createdAt: event.createdAt,
    }));
  }, [monthlyEvents]);

  // Generate day-event pairings for calendar
  const dayEventPairings = useMemo(() => {
    return generateDayEventList(monthlyEvents);
  }, [monthlyEvents]);

  // Update handlers
  const updateView = useCallback((newView: EventViewType) => {
    setView(newView);
    setRetryCount(0); // Reset retry count when changing views
  }, []);

  const updateSearchQuery = useCallback((query: string) => {
    table.setSearch(query);
    if (paginationModel.page !== 0) table.setPagination({ ...paginationModel, page: 0 });
  }, [table, paginationModel]);

  const updateFilterModel = useCallback((newFilter: Partial<IEventFilterModel>) => {
    table.setFilter({ ...(filterModel as any), ...(newFilter as any) });
    if (paginationModel.page !== 0) table.setPagination({ ...paginationModel, page: 0 });
  }, [table, filterModel, paginationModel]);

  const updateSortModel = useCallback((newSort: IEventSortModel[]) => {
    table.setSort(newSort as any);
    if (paginationModel.page !== 0) table.setPagination({ ...paginationModel, page: 0 });
  }, [table, paginationModel]);

  const updatePaginationModel = useCallback((newPagination: IPaginationModel) => {
    table.setPagination(newPagination);
  }, [table]);

  // Calendar-specific handlers
  const updateSelectedMonth = useCallback((month: Date) => {
    setSelectedMonth(month);
    const newMonth = month.getMonth() + 1;
    const newYear = month.getFullYear();
    table.setFilter({ ...(filterModel as any), month: newMonth, year: newYear });
    if (paginationModel.page !== 0) table.setPagination({ ...paginationModel, page: 0 });
  }, [table, filterModel, paginationModel]);

  const updateSelectedEvent = useCallback((event: IEvent | null) => {
    setSelectedEvent(event);
  }, []);

  // Event action handlers
  const handleCreateEvent = useCallback(async (formData: FormData) => {
    try {
      const result = await createEvent(formData).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to create event:', error);
      throw error;
    }
  }, [createEvent]);

  const handleUpdateEvent = useCallback(async (formData: FormData) => {
    try {
      const result = await updateEvent(formData).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to update event:', error);
      throw error;
    }
  }, [updateEvent]);

  const handleDeleteEvent = useCallback(async (event: IEvent) => {
    try {
      await deleteEvent(event).unwrap();
    } catch (error) {
      console.error('Failed to delete event:', error);
      throw error;
    }
  }, [deleteEvent]);

  // Pagination handlers
  const hasNextPage = useMemo(() => {
    if (!eventsData) return false;
    const totalPages = Math.ceil(eventsData.count / paginationModel.pageSize);
    return paginationModel.page < totalPages - 1;
  }, [eventsData, paginationModel]);

  const hasPreviousPage = useMemo(() => {
    return paginationModel.page > 0;
  }, [paginationModel]);

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      table.setPagination({ ...paginationModel, page: paginationModel.page + 1 });
    }
  }, [hasNextPage, table, paginationModel]);

  const goToPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      table.setPagination({ ...paginationModel, page: paginationModel.page - 1 });
    }
  }, [hasPreviousPage, table, paginationModel]);

  // Retry functionality
  const canRetry = retryCount < maxRetries;
  
  const manualRetry = useCallback(() => {
    if (canRetry) {
      setRetryCount(prev => prev + 1);
      refetchEvents();
    }
  }, [canRetry, refetchEvents]);

  // Refresh data
  const refresh = useCallback(() => {
    setRetryCount(0);
    refetchEvents();
  }, [refetchEvents]);

  // Auto-retry on error
  useEffect(() => {
    if (enhancedError && retryCount < maxRetries) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        refresh();
      }, Math.pow(2, retryCount) * 1000); // Exponential backoff
      
      return () => clearTimeout(timer);
    }
  }, [enhancedError, retryCount, maxRetries, refresh]);

  // Update selected event when data changes (matching admin-web)
  useEffect(() => {
    if (selectedEvent && eventsData?.data) {
      const updatedEvent = eventsData.data.find((event: IEvent) => event.id === selectedEvent.id);
      if (updatedEvent && JSON.stringify(updatedEvent) !== JSON.stringify(selectedEvent)) {
        setSelectedEvent(updatedEvent);
      }
    }
  }, [eventsData, selectedEvent]);

  // Manual events list fetching (matching admin-web pattern)
  const fetchEventsList = useCallback((queryParam: string) => {
    getEventsList(queryParam);
  }, [getEventsList]);

  return {
    // Data
    data: eventsData || { data: [], count: 0 },
    tableRows,
    monthlyEvents,
    monthFilteredRows,
    dayEventPairings,
    selectedEvent,
    
    // Loading states
    isLoading: isEventsLoading || isCreating || isUpdating || isDeleting,
    isCreating,
    isUpdating,
    isDeleting,
    
    // Error handling
    enhancedError,
    retryCount,
    canRetry,
    manualRetry,
    
    // UI state
    view,
    searchQuery,
    filterModel,
    sortModel,
    paginationModel,
    selectedMonth,
    
    // UI handlers
    updateView,
    updateSearchQuery,
    updateFilterModel,
    updateSortModel,
    updatePaginationModel,
    updateSelectedMonth,
    updateSelectedEvent,
    
    // Pagination
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    
    // Event actions
    handleCreateEvent,
    handleUpdateEvent,
    handleDeleteEvent,
    
    // Manual fetching
    fetchEventsList,
    
    // Refresh
    refresh,
    
    // Constants for UI
    calendarKeys: CALENDAR_KEYS,
    eventStatuses: EVENT_STATUSES,
    
    // Utility functions
    formatEventType,
    formatAttendeeLimit,
  };
};
